import { useCallback } from 'react';

import { SUCCESS_FEEDBACK_MESSAGES } from '@constants/translation.constants';
import { Availability, BusinessHoursInput } from '@generated/globalTypes';
import { UpdateAgentMutation, UpdateAgentMutationVariables } from '@generated/UpdateAgentMutation';
import { UPDATE_AGENT_MUTATION } from '@graphql/mutations/UpdateAgentMutation';
import { useGraphMutationWithFeedback } from '@hooks/useMutation';
import { useTranslation } from 'react-i18next';

import { UseAgentWorkingHoursProps, UseAgentWorkingHoursReturnType } from '.';

/**
 * Hook to update agent's working hours given a user's id.
 * @param useAgentWorkingHoursProps - destructured parameter
 * @returns an object holding functions responsible for updating agent's working hours, and its states
 */
export function useAgentWorkingHours({
  id: ID,
  businessHours,
}: UseAgentWorkingHoursProps): UseAgentWorkingHoursReturnType {
  const { t } = useTranslation();
  const [mutateBusinessHours, { loading, error }] = useGraphMutationWithFeedback<
    UpdateAgentMutation,
    UpdateAgentMutationVariables
  >(UPDATE_AGENT_MUTATION, { type: 'snackbar' });

  const mutateBusinessHoursBase = useCallback(
    async (businessHoursInput: BusinessHoursInput) => {
      await mutateBusinessHours(
        {
          variables: {
            input: {
              ID,
              businessHours: businessHoursInput,
            },
          },
        },
        {
          successMessage: t(SUCCESS_FEEDBACK_MESSAGES.UPDATE_SUCCESS, {
            name: t('user_profile.working_hours.title'),
          }),
        }
      );
    },
    [ID, mutateBusinessHours, t]
  );

  const mutateBusinessHoursToOpened = useCallback(async () => {
    await mutateBusinessHoursBase({ availability: Availability.ALWAYS_OPENED });
  }, [mutateBusinessHoursBase]);

  const mutateBusinessHoursToClosed = useCallback(async () => {
    await mutateBusinessHoursBase({ availability: Availability.ALWAYS_CLOSED });
  }, [mutateBusinessHoursBase]);

  const mutateBusinessHoursToCustom = useCallback(async () => {
    await mutateBusinessHoursBase({
      availability: Availability.CUSTOM,
      slots: businessHours.slots.map(({ __typename: _, ...slot }) => slot),
      timezone: businessHours.timezone,
    });
  }, [businessHours.slots, businessHours.timezone, mutateBusinessHoursBase]);

  return {
    mutateBusinessHoursToOpened,
    mutateBusinessHoursToClosed,
    mutateBusinessHoursToCustom,
    errorBusinessHours: error,
    loading,
  };
}
